import React, { useState } from 'react';
import KivoBall from '../components/KivoBall';

type BallState = 'idle' | 'thinking' | 'tool_calling' | 'streaming' | 'done';

const STATES: { key: BallState; label: string; hint: string }[] = [
  { key: 'idle',         label: 'Idle',         hint: 'Slow conic swirl, faint coral halo, no rings' },
  { key: 'thinking',     label: 'Thinking',     hint: 'Lava-lamp asymmetric morph, faster internal churn, no rings' },
  { key: 'tool_calling', label: 'Tool calling', hint: 'Gather → send burst, sharp teal sonar pings' },
  { key: 'streaming',    label: 'Streaming',    hint: 'Syllable cadence pulse + outward emission ripples' },
  { key: 'done',         label: 'Done',         hint: 'One-shot coordinated bloom: body, halo, ring' },
];

export default function KivoBallDemoPage() {
  const [state, setState] = useState<BallState>('idle');
  const [size, setSize] = useState(320);
  const [bumpKey, setBumpKey] = useState(0);

  const replay = () => { setBumpKey((k) => k + 1); };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--c-cream, #F8F4EE)',
        color: 'var(--c-charcoal, #1F2421)',
        fontFamily: "Inter, -apple-system, system-ui, sans-serif",
        padding: '48px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 32,
      }}
    >
      <header style={{ textAlign: 'center', maxWidth: 560 }}>
        <p
          style={{
            margin: 0,
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--c-charcoal-60, rgba(31,36,33,.6))',
          }}
        >
          Kivo · component demo
        </p>
        <h1
          style={{
            margin: '8px 0 4px',
            fontFamily: "Fraunces, Georgia, serif",
            fontSize: 44,
            letterSpacing: '-0.02em',
            lineHeight: 1,
          }}
        >
          KivoBall states
        </h1>
        <p style={{ margin: 0, color: 'var(--c-charcoal-60, rgba(31,36,33,.6))' }}>
          Switch states and resize to see how the lava orb behaves.
        </p>
      </header>

      {/* Stage */}
      <div
        style={{
          width: '100%',
          maxWidth: 720,
          aspectRatio: '1 / 1',
          maxHeight: 480,
          display: 'grid',
          placeItems: 'center',
          background: 'var(--c-cream-weak, #FBF8F4)',
          border: '1px solid var(--c-charcoal-12, rgba(31,36,33,.12))',
          borderRadius: 22,
          boxShadow: 'var(--sh-soft, 0 2px 8px rgba(31,36,33,.04), 0 8px 24px rgba(31,36,33,.06))',
        }}
      >
        {/* Only remount for `done` replays — other state swaps tween smoothly. */}
        <KivoBall key={state === 'done' ? `done-${bumpKey}` : 'persistent'} state={state} size={size} />
      </div>

      {/* State buttons */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          justifyContent: 'center',
          maxWidth: 720,
        }}
      >
        {STATES.map((s) => {
          const active = s.key === state;
          return (
            <button
              key={s.key}
              onClick={() => {
                setState(s.key);
                if (s.key === 'done') setBumpKey((k) => k + 1);
              }}
              title={s.hint}
              style={{
                appearance: 'none',
                cursor: 'pointer',
                padding: '10px 16px',
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 500,
                border: '1px solid',
                borderColor: active
                  ? 'var(--c-kivo-teal, #2AA4A6)'
                  : 'var(--c-charcoal-12, rgba(31,36,33,.12))',
                background: active ? 'var(--c-kivo-teal, #2AA4A6)' : '#fff',
                color: active ? '#fff' : 'var(--c-charcoal, #1F2421)',
                transition: 'all 180ms cubic-bezier(.22,.61,.36,1)',
              }}
            >
              {s.label}
            </button>
          );
        })}
        <button
          onClick={replay}
          style={{
            appearance: 'none',
            cursor: 'pointer',
            padding: '10px 16px',
            borderRadius: 999,
            fontSize: 14,
            fontWeight: 500,
            border: '1px dashed var(--c-charcoal-30, rgba(31,36,33,.3))',
            background: 'transparent',
            color: 'var(--c-charcoal, #1F2421)',
          }}
        >
          Replay
        </button>
      </div>

      {/* Hint for current state */}
      <p
        style={{
          margin: 0,
          fontSize: 13,
          color: 'var(--c-charcoal-60, rgba(31,36,33,.6))',
          fontStyle: 'italic',
        }}
      >
        {STATES.find((s) => s.key === state)?.hint}
      </p>

      {/* Size slider */}
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          fontSize: 13,
          color: 'var(--c-charcoal-60, rgba(31,36,33,.6))',
        }}
      >
        <span style={{ minWidth: 56, textAlign: 'right' }}>Size</span>
        <input
          type="range"
          min={48}
          max={420}
          step={1}
          value={size}
          onChange={(e) => { setSize(Number(e.target.value)); }}
          style={{ width: 280, accentColor: 'var(--c-kivo-teal, #2AA4A6)' }}
        />
        <span style={{ minWidth: 56 }}>{size}px</span>
      </label>

      {/* Side-by-side gallery of all states at small size */}
      <section
        style={{
          marginTop: 16,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 24,
          justifyContent: 'center',
        }}
      >
        {STATES.map((s) => (
          <div
            key={s.key}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              padding: 16,
              borderRadius: 14,
              background: '#fff',
              border: '1px solid var(--c-charcoal-12, rgba(31,36,33,.12))',
              minWidth: 140,
            }}
          >
            <KivoBall state={s.key} size={84} />
            <span
              style={{
                fontSize: 11,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'var(--c-charcoal-60, rgba(31,36,33,.6))',
              }}
            >
              {s.label}
            </span>
          </div>
        ))}
      </section>
    </div>
  );
}
